/* Diff PURO de manifest (subset diffable) — client-safe, consumido pelo admin-service (computa o
   diff da submissão vs versão anterior) e potencialmente pelo admin-web (ADR-0475). CÓPIA fiel da
   função do pack-registry (`versions/version-diff.ts`, ADR-0468) — o pack-registry NÃO é importável
   (service→service, §4.3); a paridade é travada por teste (manifest-diff.test.ts). O retorno espelha
   o `versionDiffDto` (ADR-0473). */
import { z } from 'zod';

export interface DiffableManifest {
  readonly version: string;
  readonly stack: { readonly minAppVersion: string };
  readonly appliesTo?: {
    readonly packId: string;
    readonly minVersion: string;
    readonly maxVersion?: string;
  };
  readonly distribution: {
    readonly channels: readonly string[];
    readonly requiredCapabilities?: readonly string[];
  };
}

/* Snapshot diffable persistido na submissão (`manifest_snapshot` jsonb, ADR-0475). Valida só o que
   o diff consome (subset) — snapshots históricos não são rejeitados por evolução de schema. */
export const manifestSnapshotDto = z.object({
  version: z.string(),
  stack: z.object({ minAppVersion: z.string() }),
  appliesTo: z
    .object({
      packId: z.string(),
      minVersion: z.string(),
      maxVersion: z.string().optional(),
    })
    .optional(),
  distribution: z.object({
    channels: z.array(z.string()),
    requiredCapabilities: z.array(z.string()).optional(),
  }),
});
export type ManifestSnapshot = z.infer<typeof manifestSnapshotDto>;

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

/** Guard ESTRUTURAL do subset diffable (revalida jsonb opaco na borda, §4.4). */
export function isDiffableManifest(value: unknown): value is DiffableManifest {
  if (typeof value !== 'object' || value === null) return false;
  /* Casts de SHAPE explícito (não index signature): dot access é válido no TS
     (sem TS4111 de `noPropertyAccessFromIndexSignature`) e satisfaz o biome. */
  const m = value as {
    version?: unknown;
    stack?: unknown;
    appliesTo?: unknown;
    distribution?: unknown;
  };
  if (typeof m.version !== 'string') return false;
  const stack = m.stack;
  if (typeof stack !== 'object' || stack === null) return false;
  if (typeof (stack as { minAppVersion?: unknown }).minAppVersion !== 'string') return false;
  if (m.appliesTo !== undefined) {
    const appliesTo = m.appliesTo;
    if (typeof appliesTo !== 'object' || appliesTo === null) return false;
    const applicability = appliesTo as {
      packId?: unknown;
      minVersion?: unknown;
      maxVersion?: unknown;
    };
    if (typeof applicability.packId !== 'string') return false;
    if (typeof applicability.minVersion !== 'string') return false;
    if (applicability.maxVersion !== undefined && typeof applicability.maxVersion !== 'string') {
      return false;
    }
  }
  const dist = m.distribution;
  if (typeof dist !== 'object' || dist === null) return false;
  const d = dist as { channels?: unknown; requiredCapabilities?: unknown };
  if (!isStringArray(d.channels)) return false;
  if (d.requiredCapabilities !== undefined && !isStringArray(d.requiredCapabilities)) {
    return false;
  }
  return true;
}

export interface ManifestDiff {
  readonly from: string;
  readonly to: string;
  readonly capabilitiesAdded: readonly string[];
  readonly capabilitiesRemoved: readonly string[];
  readonly minAppVersionChange?: { readonly from: string; readonly to: string };
  readonly channelsAdded: readonly string[];
  readonly channelsRemoved: readonly string[];
}

/** Itens em `next` ausentes em `base`, ordenados (ordem estável p/ UI/testes). */
function added(base: readonly string[], next: readonly string[]): string[] {
  const baseSet = new Set(base);
  return next.filter((item) => !baseSet.has(item)).sort();
}

/** Diff de `a` (from) para `b` (to). Base = `a`, alvo = `b`. */
export function diffManifests(a: DiffableManifest, b: DiffableManifest): ManifestDiff {
  const capsA = a.distribution.requiredCapabilities ?? [];
  const capsB = b.distribution.requiredCapabilities ?? [];
  const minChanged = a.stack.minAppVersion !== b.stack.minAppVersion;
  return {
    from: a.version,
    to: b.version,
    capabilitiesAdded: added(capsA, capsB),
    capabilitiesRemoved: added(capsB, capsA),
    ...(minChanged && {
      minAppVersionChange: { from: a.stack.minAppVersion, to: b.stack.minAppVersion },
    }),
    channelsAdded: added(a.distribution.channels, b.distribution.channels),
    channelsRemoved: added(b.distribution.channels, a.distribution.channels),
  };
}
